// Chat lock (like WhatsApp's): locked chats leave the chat list and search,
// live behind "Locked chats" and a secret code, and their notifications say
// only "New message". The code and the list stay on this device (the code is
// kept as a PBKDF2 hash); the list is mirrored into the chat store so the
// service worker can hide push notifications too.
import { signal } from "@preact/signals";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import * as S from "../state";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";

interface Lock {
  salt?: string;
  hash?: string;
  chats: string[];
}
let account = "";
const key = () => "xmm.chatlock." + account;
const ITER = 150_000;

function read(): Lock {
  try {
    return {
      chats: [],
      ...(JSON.parse(localStorage.getItem(key()) ?? "{}") as Partial<Lock>),
    };
  } catch {
    return { chats: [] };
  }
}
function write(l: Lock) {
  try {
    localStorage.setItem(key(), JSON.stringify(l));
  } catch {
    /* storage blocked */
  }
  S.lockedChats.value = new Set(l.chats);
  void engine.value?.saveLockedChats(l.chats);
}

export function loadChatLock(jid: string) {
  account = jid;
  S.lockedChats.value = new Set(read().chats);
  S.lockOpen.value = false;
}
export const hasCode = () => !!read().hash;

async function derive(code: string, salt: Uint8Array): Promise<string> {
  const k = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(code),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt: salt as BufferSource,
      iterations: ITER,
    },
    k,
    256,
  );
  return btoa(String.fromCharCode(...new Uint8Array(bits)));
}
async function setCode(code: string) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  write({
    ...read(),
    salt: btoa(String.fromCharCode(...salt)),
    hash: await derive(code, salt),
  });
}
async function check(code: string) {
  const l = read();
  if (!l.salt || !l.hash) return false;
  return (
    (await derive(
      code,
      Uint8Array.from(atob(l.salt), (c) => c.charCodeAt(0)),
    )) === l.hash
  );
}

/** Lock a chat (the first time: set the secret code), or unlock it. */
export function toggleLock(jid: string) {
  const l = read();
  if (l.chats.includes(jid)) {
    write({ ...l, chats: l.chats.filter((j) => j !== jid) });
    return toast("Chat unlocked");
  }
  if (!l.hash) return void (lockDialog.value = { mode: "create", then: jid });
  write({ ...l, chats: [...l.chats, jid] });
  if (S.activeChat.value === jid && !S.lockOpen.value)
    void engine.value?.openChat(null);
  toast("Chat locked: find it under Locked chats");
}

export const lockDialog = signal<{
  mode: "create" | "enter";
  then?: string;
} | null>(null);

/** The secret-code dialog: create it (and lock the chat), or enter it. */
export function LockDialog() {
  const d = lockDialog.value!;
  const [code, setCodeIn] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => input.current?.focus(), []);
  const close = () => (lockDialog.value = null);
  async function submit(e: Event) {
    e.preventDefault();
    setError("");
    if (d.mode === "create") {
      if (code.length < 4) return setError("Use at least 4 characters.");
      if (code !== again) return setError("The codes don't match.");
      setBusy(true);
      await setCode(code);
      close();
      if (d.then) toggleLock(d.then);
      return;
    }
    setBusy(true);
    if (await check(code)) {
      S.lockOpen.value = true;
      close();
      if (d.then) void engine.value?.openChat(d.then);
      else S.showLocked.value = true;
    } else {
      setBusy(false);
      setError("That's not your secret code.");
    }
  }
  return (
    <div class="modal-backdrop" onClick={close}>
      <div
        class="modal lock-dialog"
        role="dialog"
        aria-label="Secret code"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={(e) => void submit(e)}>
          <span class="lock-icon">
            <Icon name="lock" size={28} />
          </span>
          <h2>
            {d.mode === "create"
              ? "Create a secret code"
              : "Enter your secret code"}
          </h2>
          <p class="muted small">
            {d.mode === "create"
              ? "You'll need it to open locked chats on this device."
              : "To see your locked chats."}
          </p>
          <input
            ref={input}
            class="line-input"
            type="password"
            value={code}
            onInput={(e) => setCodeIn(e.currentTarget.value)}
            placeholder="Secret code"
            aria-label="Secret code"
          />
          {d.mode === "create" && (
            <input
              class="line-input"
              type="password"
              value={again}
              onInput={(e) => setAgain(e.currentTarget.value)}
              placeholder="Confirm secret code"
              aria-label="Confirm secret code"
            />
          )}
          {error && <p class="error">{error}</p>}
          <div class="row-buttons">
            <button type="button" class="link" onClick={close}>
              Cancel
            </button>
            <button class="primary" disabled={busy || !code}>
              {d.mode === "create" ? "Create and lock" : "Unlock"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Shown instead of a locked chat that was opened directly (notification, link). */
export function LockedGate({ jid }: { jid: string }) {
  return (
    <div class="locked-gate">
      <span class="lock-icon">
        <Icon name="lock" size={36} />
      </span>
      <h2>This chat is locked</h2>
      <button
        class="primary"
        onClick={() => (lockDialog.value = { mode: "enter", then: jid })}
      >
        Unlock
      </button>
    </div>
  );
}

/** Chats you can see right now (locked ones only while unlocked). */
export const visibleChat = (jid: string) =>
  !S.lockedChats.value.has(jid) || S.lockOpen.value;

/** Re-lock when the window is hidden. */
if (typeof document !== "undefined")
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden")
      ((S.lockOpen.value = false), (S.showLocked.value = false));
  });
